import { useState } from 'react'
import { X } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { clsx } from 'clsx'

const FIELD_TYPES = [
  { value: 'TEXT', label: 'Text' },
  { value: 'TEXTAREA', label: 'Paragraph' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'INTEGER', label: 'Whole number' },
  { value: 'DECIMAL', label: 'Decimal' },
  { value: 'DATE', label: 'Date' },
  { value: 'DATETIME', label: 'Date/time' },
  { value: 'SELECT', label: 'Dropdown' },
  { value: 'MULTI_SELECT', label: 'Multi-select' },
  { value: 'BOOLEAN', label: 'Checkbox' },
  { value: 'USER', label: 'User' },
  { value: 'URL', label: 'URL' },
] as const

export type CustomFieldType = (typeof FIELD_TYPES)[number]['value']

export interface CreateFieldModalProps {
  open: boolean
  onClose: () => void
  onCreate: (payload: {
    name: string
    type: string
    config?: Record<string, any>
    isRequired?: boolean
  }) => Promise<void>
  projectId?: string
  organizationId?: string
}

export function CreateFieldModal({
  open,
  onClose,
  onCreate,
}: CreateFieldModalProps) {
  const [name, setName] = useState('')
  const [fieldType, setFieldType] = useState<CustomFieldType>('TEXT')
  const [isRequired, setIsRequired] = useState(false)
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setSaving(true)
    try {
      await onCreate({
        name: name.trim(),
        type: fieldType,
        config: {},
        isRequired,
      })
      reset()
    } finally {
      setSaving(false)
    }
  }

  const reset = () => {
    setName('')
    setFieldType('TEXT')
    setIsRequired(false)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-xs"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-4">
          <h2 className="text-base font-bold text-[#172B4D]">Create a field</h2>
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'rounded-lg p-1.5 text-[#6B778C] hover:text-[#172B4D] hover:bg-gray-100 transition-colors'
            )}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 pb-5 space-y-4">
          {/* Field name */}
          <div>
            <label className="block text-xs font-semibold text-[#172B4D] mb-1">
              Field name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Story points"
              required
              autoFocus
              className="w-full rounded-lg border border-[#DFE1E6] px-3 py-2 text-sm text-[#172B4D] placeholder:text-[#6B778C] focus:border-[#0052CC] focus:ring-1 focus:ring-[#0052CC] focus:outline-none"
            />
          </div>

          {/* Field type */}
          <div>
            <label className="block text-xs font-semibold text-[#172B4D] mb-1">
              Field type
            </label>
            <select
              value={fieldType}
              onChange={(e) => setFieldType(e.target.value as CustomFieldType)}
              className="w-full rounded-lg border border-[#DFE1E6] px-3 py-2 text-sm text-[#172B4D] bg-white focus:border-[#0052CC] focus:ring-1 focus:ring-[#0052CC] focus:outline-none"
            >
              {FIELD_TYPES.map((ft) => (
                <option key={ft.value} value={ft.value}>
                  {ft.label}
                </option>
              ))}
            </select>
          </div>

          {/* Required checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="field-required"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              className="h-4 w-4 rounded border-[#DFE1E6] text-[#0052CC] focus:ring-[#0052CC]"
            />
            <label htmlFor="field-required" className="text-sm text-[#172B4D] cursor-pointer select-none">
              Required field
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                reset()
                onClose()
              }}
              className="rounded-lg px-4 py-2 text-sm font-medium text-[#42526E] hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || saving}
              className="rounded-lg bg-[#0052CC] px-5 py-2 text-sm font-semibold text-white hover:bg-[#0052CC]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Creating…' : 'Create field'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
